import React from "react";
import { Link } from "react-router-dom";

/**
 * Napoli BrandMark — inline SVG (no image dependencies).
 *
 * Layout (240×120 viewBox):
 *   - Olive branch (5 leaves alternating, 80px wide, hand-drawn stroke)
 *   - Wordmark: NAPOLI, Cormorant Garamond 700, letter-spacing 0.15em
 *   - Thin oxblood horizontal rule (60px wide)
 *   - "EST. 2019 · CUCINA" in Inter 600, 9px, letter-spacing 0.2em
 *
 * Props:
 *   size:  'sm' | 'md' | 'lg'   default 'md'
 *   theme: 'light' | 'dark'     default 'light'
 *           - light: oxblood wordmark on transparent/cream surface
 *           - dark:  cream wordmark on dark/espresso surface
 *   to:    optional route path (e.g. "/"). When set, wraps the SVG in a
 *          <Link to={to}> so the logo becomes clickable.
 */
const SIZE_PX = {
  sm: { w: 120, h: 60 },
  md: { w: 200, h: 100 },
  lg: { w: 320, h: 160 },
};

const WORDMARK_PX = {
  sm: 18,
  md: 28,
  lg: 44,
};

export const BrandMark = ({
  size = "md",
  theme = "light",
  className = "",
  ariaLabel = "Napoli",
  to,
  linkTestId,
}) => {
  const dims = SIZE_PX[size] || SIZE_PX.md;
  const wordPx = WORDMARK_PX[size] || WORDMARK_PX.md;

  const wordmarkColor = theme === "dark" ? "#F5EBDA" : "#8B1E2C";
  const ruleColor = theme === "dark" ? "#C5A04D" : "#8B1E2C";
  const microColor = theme === "dark" ? "#F5EBDA" : "#8B1E2C";
  const oliveColor = "#C5A04D"; // antique gold in both themes

  const svg = (
    <svg
      data-testid={`brand-mark-${size}-${theme}`}
      role="img"
      aria-label={ariaLabel}
      viewBox="0 0 240 120"
      width={dims.w}
      height={dims.h}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <title>{ariaLabel}</title>

      {/* --- Olive branch (centered, ~80px wide, top portion of the mark) --- */}
      <g
        transform="translate(80 8)"
        fill="none"
        stroke={oliveColor}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* main branch */}
        <path d="M0 18 C 20 14, 60 14, 80 18" />
        {/* leaves: 5 alternating */}
        <path d="M14 17 C 8 8, 18 6, 22 14 C 18 17, 14 17, 14 17 Z" />
        <path d="M30 19 C 32 28, 22 30, 22 22 C 26 19, 30 19, 30 19 Z" />
        <path d="M42 16 C 38 7, 50 5, 52 14 C 48 18, 42 16, 42 16 Z" />
        <path d="M56 19 C 58 28, 48 30, 48 22 C 52 19, 56 19, 56 19 Z" />
        <path d="M68 17 C 64 8, 76 6, 78 15 C 74 18, 68 17, 68 17 Z" />
        {/* tiny berry */}
        <circle cx="80" cy="18" r="1.5" fill={oliveColor} stroke="none" />
      </g>

      {/* --- Wordmark NAPOLI --- */}
      <text
        x="120"
        y="74"
        textAnchor="middle"
        fill={wordmarkColor}
        fontFamily='"Cormorant Garamond", Georgia, serif'
        fontWeight="700"
        fontSize={wordPx}
        letterSpacing={`${wordPx * 0.15}`}
        style={{ fontFeatureSettings: '"lnum"' }}
      >
        NAPOLI
      </text>

      {/* --- Oxblood horizontal rule (60px wide, centered) --- */}
      <line
        x1="90"
        y1="88"
        x2="150"
        y2="88"
        stroke={ruleColor}
        strokeWidth="1"
      />

      {/* --- Microcaps line --- */}
      <text
        x="120"
        y="102"
        textAnchor="middle"
        fill={microColor}
        fontFamily="Inter, sans-serif"
        fontWeight="600"
        fontSize="8"
        letterSpacing="1.6"
      >
        EST. 2019 · CUCINA
      </text>
    </svg>
  );

  if (to) {
    return (
      <Link
        to={to}
        data-testid={linkTestId || "brand-mark-link"}
        aria-label={ariaLabel}
        className="inline-flex items-center group focus:outline-none focus-visible:ring-2 focus-visible:ring-napoli-oxblood/50"
      >
        {svg}
      </Link>
    );
  }

  return svg;
};

export default BrandMark;
